import { network } from "../../config";
import { TypeDialogMessage } from "../../types/DialogChat";
import { TypeUserData } from "../../types/TypeUsers";
import socket from "../../utils/socket";

const { chat, socketIo } = network;

export type DialogNewMessageEvent = {
  newMessage: TypeDialogMessage & {
    chatId?: string;
  };
};

export type DialogMessageReactionEvent = {
  messageId: string;
  emoji: string;
  reactBy: Pick<TypeUserData, "name" | "surname" | "_id">;
  reactionAdded: boolean;
};

export type DialogMessagesReadEvent = {
  updatedMessages: {
    _id: string;
    readBy: string[];
  }[];
};

export type DialogMessageEditedEvent = {
  chatId: string;
  messageId: string;
  updatedMessage: TypeDialogMessage;
};

export type DialogMessageDeletedEvent = {
  chatId: string;
  messageId: string;
  deletedBy: string;
};

export type DialogMessagePinnedEvent = {
  chatId: string;
  pinnedMessage: {
    message: string;
    pinnedBy: string;
    pinnedAt: string;
  };
};

export type DialogMessageUnpinnedEvent = {
  chatId: string;
  messageId: string;
};

export type DialogMessageMentionedEvent = {
  chatId: string;
  messageId: string;
  mentionedBy: string;
};

export type DialogTypingEvent = {
  chatId: string;
  userId: string;
};

type DialogSocketEventMap = {
  messageReceived: DialogNewMessageEvent;
  messageReacted: DialogMessageReactionEvent;
  messagesRead: DialogMessagesReadEvent;
  messageEdited: DialogMessageEditedEvent;
  messageDeleted: DialogMessageDeletedEvent;
  messagePinned: DialogMessagePinnedEvent;
  messageUnpinned: DialogMessageUnpinnedEvent;
  messageMentioned: DialogMessageMentionedEvent;
  typingStarted: DialogTypingEvent;
  typingStopped: DialogTypingEvent;
};

type SocketLike = {
  emit: (eventName: string, payload?: unknown) => void;
  on: (eventName: string, handler: (payload: any) => void) => void;
  off: (eventName: string, handler: (payload: any) => void) => void;
};

const eventNames: Record<keyof DialogSocketEventMap, string> = {
  messageReceived: chat.newDialogMessage,
  messageReacted: chat.onMessageReacted,
  messagesRead: socketIo.messagesRead,
  messageEdited: socketIo.dialogMessageEdited,
  messageDeleted: socketIo.dialogMessageDeleted,
  messagePinned: socketIo.dialogMessagePinned,
  messageUnpinned: socketIo.dialogMessageUnpinned,
  messageMentioned: socketIo.dialogMessageMentioned,
  typingStarted: socketIo.dialogChatTypingStarted,
  typingStopped: socketIo.dialogChatTypingStopped,
};

const createSubscription =
  <EventName extends keyof DialogSocketEventMap>(
    socketClient: SocketLike,
    eventName: EventName,
  ) =>
  (handler: (payload: DialogSocketEventMap[EventName]) => void) => {
    const rawEventName = eventNames[eventName];
    socketClient.on(rawEventName, handler);

    return () => {
      socketClient.off(rawEventName, handler);
    };
  };

export const createDialogChatSocketAdapter = (
  socketClient: SocketLike = socket,
) => ({
  joinUserDialogChannel(currentUserId: string) {
    socketClient.emit(chat.joinDialogChat, currentUserId);
  },
  reactToMessage(payload: {
    userId: string;
    messageId: string;
    emoji: string;
  }) {
    socketClient.emit(chat.reactOnMessage, payload);
  },
  onMessageReceived: createSubscription(socketClient, "messageReceived"),
  onMessageReacted: createSubscription(socketClient, "messageReacted"),
  onMessagesRead: createSubscription(socketClient, "messagesRead"),
  onMessageEdited: createSubscription(socketClient, "messageEdited"),
  onMessageDeleted: createSubscription(socketClient, "messageDeleted"),
  onMessagePinned: createSubscription(socketClient, "messagePinned"),
  onMessageUnpinned: createSubscription(socketClient, "messageUnpinned"),
  onMessageMentioned: createSubscription(socketClient, "messageMentioned"),
  onTypingStarted: createSubscription(socketClient, "typingStarted"),
  onTypingStopped: createSubscription(socketClient, "typingStopped"),
});

export const dialogChatSocketAdapter = createDialogChatSocketAdapter();

export type DialogChatSocketAdapter = ReturnType<
  typeof createDialogChatSocketAdapter
>;
